import React, { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { Typography } from '@saucelabs/ds-typography';
import { Loader } from '@saucelabs/sl-component-loader';
import { useFragment, useMutation } from '@apollo/client';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Link, useParams } from 'react-router-dom';
import { IconImageOutline } from '@saucelabs/ds-icons';
import PlatformIcon from '@saucelabs/sl-component-platform-icon';
import { push } from 'connected-react-router';

import {
  BuildSnapshotThumbFragFragment,
  DiffStatus,
  Rect,
  UpdateDiffStatus,
} from '../../graphql/__generated__/graphql';
import { graphql } from '../../graphql/__generated__';
import { NotNullProperties } from '../../global/types';
import { urls } from '../../constants';
import { useVisualDispatch } from '../../reduxHooks';
import { actions } from '../../ducks/buildDetails';
import DecideButton, { DecideButtonTypes } from '../common/DecideButton';
import { titleCase } from '../../global/utils';
import Metadata from '../common/Metadata';
import BuildSnapshotThumbIndicator from './BuildSnapshotThumbIndicator';
import { BuildSnapshotThumbChangeOverlay } from './BuildSnapshotThumbChangeOverlay';

const UpdateDiffThumbMutation = graphql(/* GraphQL */ `
  mutation UpdateDiffThumbMutation($uuid: UUID!, $status: UpdateDiffStatus!) {
    updateDiff(input: { uuid: $uuid, status: $status }) {
      id
      status
      build {
         ...BuildsViewBuildFragment
      }
    }
  }
`);

export const BuildSnapshotThumbFrag = graphql(`
  fragment BuildSnapshotThumbFrag on Diff {
    id
    status
    diffClusters {
      x
      y
      width
      height
    }
    snapshot {
      id
      name
      buildId
      testName
      suiteName
      operatingSystem
      operatingSystemVersion
      browser
      browserVersion
      device
      thumbnailUrl
      width
      height
      ignoreRegions {
        width
        height
        x
        y
      }
    }
    baseline {
      id
    }
  }
`);

enum ImageState {
  Loading,
  Loaded,
  Errored,
}

const noop = (ev) => { ev.stopPropagation(); };

export const BuildSnapshotThumb: FC<BuildSnapshotThumbProps> = memo((props) => {
  const { diff: nonRcDiff } = props;
  const { snapshot, id: diffId, diffClusters } = nonRcDiff;
  const { buildId } = useParams<{ buildId: string }>();
  const { data: diff } = useFragment({
    fragment: BuildSnapshotThumbFrag,
    from: {
      __typename: 'Diff',
      id: diffId,
    },
  });
  const { ignoreRegions, thumbnailUrl } = snapshot;

  const [imageState, setImageState] = useState<ImageState>(ImageState.Loading);
  const onLoad = useCallback(() => {
    setImageState(ImageState.Loaded);
  }, []);
  const onError = useCallback(() => {
    setImageState(ImageState.Errored);
  }, []);

  const dispatch = useVisualDispatch();
  const setItemsHaveChanged = useCallback(() => {
    dispatch(actions.setItemsChanged(true));
  }, [dispatch]);

  const openDiff = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    dispatch(push({
      pathname: urls.getDiff(buildId, diffId),
      search: window.location.search,
    }));
  }, [dispatch, diffId, buildId]);

  const [updateDiff, { loading }] = useMutation(UpdateDiffThumbMutation, {
    onCompleted: setItemsHaveChanged,
  });

  const testMeta = useMemo(() => {
    const lines = (
      [
        [
          'Name',
          snapshot.name,
        ],
        snapshot.suiteName && [
          'Suite',
          snapshot.suiteName,
        ],
        snapshot.operatingSystem && [
          'OS',
          <>
            {titleCase(snapshot.operatingSystem)}
            {' '}
            {snapshot.operatingSystemVersion}
          </>,
        ],
        snapshot.browser && [
          'Browser',
          <>
            {titleCase(snapshot.browser)}
            {' '}
            {snapshot.browserVersion}
          </>,
        ],
        snapshot.device && [
          'Device',
          snapshot.device,
        ],
        [
          'Changes',
          diff.diffClusters?.length,
        ],
      ] as [string, ReactNode][]
    ).filter(Boolean);
    return (
      <Typography
        component="div"
        onClick={noop}
        variant="body-small"
      >
        <table>
          <tbody>
            {lines.map(([label, value]) => (
              <tr key={label}>
                <th className="font-semibold">
                  {label}
                  :
                </th>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Typography>
    );
  }, [snapshot, diff.diffClusters?.length]);

  const approveDiff = useCallback(() => {
    void updateDiff({
      variables: {
        uuid: diffId,
        status: UpdateDiffStatus.Approved,
      },
    });
  }, [diffId, updateDiff]);

  const rejectDiff = useCallback(() => {
    void updateDiff({
      variables: {
        uuid: diffId,
        status: UpdateDiffStatus.Rejected,
      },
    });
  }, [diffId, updateDiff]);

  const unApproveDiff = useCallback(() => {
    void updateDiff({
      variables: {
        uuid: diffId,
        status: UpdateDiffStatus.Unapproved,
      },
    });
  }, [diffId, updateDiff]);

  return (
    <div className="p-1 w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5 xl:max-w-34.75">
      <div className="rounded border-solid border border-gray-200 flex flex-col relative">
        <Link
          className="cursor-pointer focus-ring rounded hover:no-underline block"
          onClick={openDiff}
          to={urls.getDiff(buildId, diffId)}
        >
          <div className="h-24 w-full relative overflow-hidden">
            {thumbnailUrl && (
              <img
                alt="diff snapshot thumbnail preview"
                className="w-full h-auto"
                loading="lazy"
                onError={onError}
                onLoad={onLoad}
                src={thumbnailUrl}
              />
            )}
            {(
              imageState === ImageState.Loaded &&
              diffClusters.length > 0 &&
              snapshot.width &&
              snapshot.height
            ) && (
              <BuildSnapshotThumbChangeOverlay
                diffClusters={diffClusters as Rect[]}
                height={snapshot.height}
                ignoreRegions={ignoreRegions as Rect[]}
                width={snapshot.width}
              />
            )}
            {imageState === ImageState.Errored && (
              <div className="flex flex-col items-center justify-center h-full px-4 text-medium-emphasis absolute left-0 top-0 bg-white">
                <IconImageOutline className="fill-current" />
                <Typography
                  className="text-center mt-2"
                  variant="body-small"
                >
                  We could not load the thumbnail for this snapshot
                </Typography>
              </div>
            )}
            <Loader
              containerClass="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
              show={imageState === ImageState.Loading}
            />
          </div>
          <div className="relative">
            <Tooltip
              placement="end"
              title={testMeta}
            >
              <div className="bg-gray-50 w-full flex flex-row flex-wrap p-1 gap-1 items-center">
                <div className="w-full">
                  <Typography
                    className="truncate text-high-emphasis font-semibold"
                    variant="body"
                  >
                    {snapshot.name}
                  </Typography>
                </div>
                <div className="flex flex-row justify-between truncate flex-1 items-center gap-1 h-4 mr-9 overflow-hidden">
                  <div className="flex items-center gap-0.5 flex-shrink min-w-0 text-medium-emphasis">
                    <Metadata
                      className="min-w-1.5"
                      hide={!snapshot.operatingSystem}
                      hideTooltip
                      icon={(
                        <PlatformIcon
                          className="fill-current"
                          name={snapshot.operatingSystem}
                        />
                      )}
                      label={snapshot.operatingSystemVersion ?? ''}
                      truncate
                    />
                    {snapshot.operatingSystem && <span />}
                    <Metadata
                      className="min-w-1.5"
                      hide={!snapshot.browser}
                      hideTooltip
                      icon={(
                        <PlatformIcon
                          className="fill-current"
                          name={snapshot.browser}
                        />
                      )}
                      label={snapshot.browserVersion ?? ''}
                      truncate
                    />
                  </div>

                  <BuildSnapshotThumbIndicator
                    count={diff.diffClusters?.length ?? 0}
                    hasBaseline={!!diff.baseline}
                  />
                </div>
              </div>
            </Tooltip>
          </div>
        </Link>
        <div className="absolute bottom-0 right-0 flex flex-row rounded border border-solid border-gray-200 m-1">
          <Tooltip
            title={diff.status === DiffStatus.Approved ? 'Unaccept' : 'Accept'}
          >
            <DecideButton
              aria-label="Approve Snapshot"
              className="rounded-tr-none rounded-br-none"
              disabled={loading}
              isActive={diff.status === DiffStatus.Approved}
              isNoChanges={diff.status === DiffStatus.Equal}
              onClick={approveDiff}
              onUnapprove={unApproveDiff}
              size="sm"
              type={DecideButtonTypes.ACCEPT}
            />
          </Tooltip>
          <Tooltip
            title={diff.status === DiffStatus.Rejected ? 'Unreject' : 'Reject'}
          >
            <DecideButton
              aria-label="Reject Snapshot"
              className="rounded-tl-none rounded-bl-none"
              disabled={loading}
              isActive={diff.status === DiffStatus.Rejected}
              onClick={rejectDiff}
              onUnapprove={unApproveDiff}
              size="sm"
              type={DecideButtonTypes.REJECT}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

BuildSnapshotThumb.displayName = 'BuildSnapshotThumb';

export interface BuildSnapshotThumbProps {
  diff: NotNullProperties<BuildSnapshotThumbFragFragment, 'snapshot'>;
}
